import { FC, PropsWithChildren, useEffect, useState } from "react";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";

import {
  LARA_DEFAULT_CONTENT_EN,
  LODA_DEFAULT_CONTENT_EN,
} from "@/lib/translations/en";
import {
  LARA_DEFAULT_CONTENT_DA,
  LODA_DEFAULT_CONTENT_DA,
} from "@/lib/translations/da";

import useTenant from "@/hooks/useTenant";
import { TxKeyPath } from "@/models/i18n/TxKeyPath";
import { Domain, getDomain } from "@/utils/domain";
import { ContentContext } from "./ContentContext";
import { useAuth0 } from "@auth0/auth0-react";

// Translations for Danish and English
i18n
  .use(initReactI18next) // Passes i18n down to react-i18next
  .init({
    lng: "da", // Default language (will be dynamically updated)
    fallbackLng: "da", // Fallback language
    interpolation: {
      escapeValue: false, // React already escapes values by default
    },
  });

const ContentProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth0();

  const { useQueryPageContent } = useTenant();

  // Only fetch page content when authenticated, otherwise use default translations
  const fetchedPageContent = useQueryPageContent(isAuthenticated);

  const [updater, setUpdater] = useState(0);

  const domain = getDomain(window.location.hostname);

  const languageResources =
    domain === Domain.LODA
      ? {
          en: {
            translation: {
              ...LODA_DEFAULT_CONTENT_EN,
            },
          },
          da: {
            translation: {
              ...LODA_DEFAULT_CONTENT_DA,
            },
          },
        }
      : {
          en: {
            translation: {
              ...LARA_DEFAULT_CONTENT_EN,
            },
          },
          da: {
            translation: {
              ...LARA_DEFAULT_CONTENT_DA,
            },
          },
        };

  //Initial default content load
  useEffect(() => {
    if (updater !== 0) return;

    i18n.addResourceBundle(
      "en",
      "translation",
      languageResources.en,
      true,
      true
    );
    i18n.addResourceBundle(
      "da",
      "translation",
      languageResources.da,
      true,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updater]);

  // Update the content when fetched
  useEffect(() => {
    if (fetchedPageContent.data && fetchedPageContent.isFetched) {
      const content = JSON.parse(fetchedPageContent.data);

      // Check if content is null or doesn't have the expected structure
      if (!content || typeof content !== "object") {
        return;
      }

      const languageResources =
        domain === Domain.LODA
          ? {
              en: {
                translation: {
                  ...LODA_DEFAULT_CONTENT_EN,
                  ...(content.en || {}),
                },
              },
              da: {
                translation: {
                  ...LODA_DEFAULT_CONTENT_DA,
                  ...(content.da || {}),
                },
              },
            }
          : {
              en: {
                translation: {
                  ...LARA_DEFAULT_CONTENT_EN,
                  ...(content.en || {}),
                },
              },
              da: {
                translation: {
                  ...LARA_DEFAULT_CONTENT_DA,
                  ...(content.da || {}),
                },
              },
            };

      i18n.addResourceBundle(
        "en",
        "translation",
        languageResources.en,
        true,
        true
      );
      i18n.addResourceBundle(
        "da",
        "translation",
        languageResources.da,
        true,
        true
      );

      setUpdater((prev) => prev + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedPageContent.data, fetchedPageContent.isFetched]);

  const translate = (tx: TxKeyPath) => t(`translation.${tx}`);

  return (
    <ContentContext.Provider value={{ t: translate }}>
      <div key={updater}> {children}</div>
    </ContentContext.Provider>
  );
};

export default ContentProvider;
